/**
 * <fg-flavor-quiz>
 *
 * Standalone flavor-matching quiz custom element. Entirely new/isolated from
 * the rest of the theme's JS - does not read from or modify any existing
 * component (wiq-bundle-builder, pi-smoothie-quiz, etc). The only thing it
 * shares with the rest of the site is the final handoff: redirecting into the
 * existing bundle builder via ?bundle_items=, per the already-built
 * WIQBundleBuilder.loadFromUrlParams() flow.
 *
 * This file is the overall structure/state machine for the 9-step quiz.
 * Per-step render/validate logic is stubbed with TODOs and gets filled in
 * page by page in follow-up work.
 */
(() => {
  const STEP_COUNT = 9;

  // Page 1's when->line mapping, locked in during design. Lunch's Smoothies
  // inclusion carries a protein-rich-only restriction, but that's a scoring
  // concern (applied later, in the actual box-building step) - here we only
  // need to know which lines a given "when" answer surfaces as a candidate.
  const WHEN_TO_LINES = {
    Breakfast: ['Overnight Oats', 'Smoothies'],
    Lunch: ['Overnight Oats', 'Garden Bowls', 'Smoothies'],
    Dinner: ['Garden Bowls'],
    Snacks: ['Smoothies', 'Delites'],
    Dessert: ['Delites'],
  };

  const LINE_ORDER = ['Smoothies', 'Overnight Oats', 'Garden Bowls', 'Delites'];

  // Klaviyo's identify() call is lenient and auto-normalizes phone numbers
  // for profile storage, but the Subscriptions API is strict and needs
  // proper E.164 (+1XXXXXXXXXX) - a bare 10-digit input silently fails to
  // apply SMS consent there while the rest of the request still succeeds.
  // Frozen Garden is US-only, so this only needs to handle US numbers.
  function toE164UsPhone(raw) {
    const trimmed = raw.trim();
    if (trimmed.startsWith('+')) return trimmed;
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return trimmed;
  }

  const STEP_LABELS = [
    'Question 1 of 9',
    'Question 2 of 9',
    'Question 3 of 9',
    'Question 4 of 9',
    'Question 5 of 9',
    'Question 6 of 9',
    'Question 7 of 9',
    'Almost there!',
    '',
  ];

  // Human-readable names for the funnel-tracking events published to GA4
  // (see publishAnalytics()) - purely a reporting label, no functional use.
  const STEP_NAMES = [
    'When & Product Lines',
    'Flavor Profile',
    'Ingredients To Skip',
    'Ingredients To Love',
    'Health Goals',
    'Variety Preference',
    'Box Size',
    'Email Capture',
    'Result',
  ];

  class FGFlavorQuiz extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0;
      this.answers = {
        when: [],                 // Page 1: Breakfast/Lunch/Dinner/Snacks/Dessert
        lines: [],                // Page 1: final (editable) product line selection
        flavors: [],              // Page 2
        allergens: [],            // Page 3 section 1
        skipIngredients: [],      // Page 3 section 2
        lovedIngredients: [],     // Page 4
        healthGoals: [],          // Page 5
        variety: null,            // Page 6: 'mix' | 'favorites'
        boxSize: null,            // Page 7: 8 | 15 | 24 | 36 | custom number
        firstName: '',            // Page 8 (optional)
        email: '',                // Page 8
        phone: '',                // Page 8
      };
    }

    connectedCallback() {
      const dataScript = this.querySelector('[data-fg-quiz-data]');
      this.data = dataScript ? JSON.parse(dataScript.textContent) : { lines: {}, taxonomies: {} };

      this.steps = Array.from(this.querySelectorAll('[data-fg-quiz-step]'));
      this.navContainer = this.querySelector('[data-fg-quiz-nav]');
      this.progressContainer = this.querySelector('[data-fg-quiz-progress]');
      this.progressFill = this.querySelector('[data-fg-quiz-progress-fill]');
      this.progressLabel = this.querySelector('[data-fg-quiz-progress-label]');
      this.backButton = this.querySelector('[data-fg-quiz-back]');
      this.continueButton = this.querySelector('[data-fg-quiz-continue]');

      this.backButton?.addEventListener('click', () => this.goBack());
      this.continueButton?.addEventListener('click', () => this.goNext());

      this.setupWhenAndLines();
      this.setupFlavorProfile();
      this.setupDietaryNeeds();
      this.setupLovedIngredients();
      this.setupHealthGoals();
      this.setupVarietyFavorites();
      this.setupBoxSize();
      this.setupEmailCapture();

      this.renderStep(this.currentStep);
    }

    // --- Navigation -----------------------------------------------------

    goNext() {
      if (!this.isStepValid(this.currentStep)) return;
      if (this.currentStep < STEP_COUNT - 1) {
        this.currentStep += 1;
        this.renderStep(this.currentStep);
      }
    }

    goBack() {
      if (this.currentStep > 0) {
        this.currentStep -= 1;
        this.renderStep(this.currentStep);
      }
    }

    goToStep(index) {
      if (index >= 0 && index < STEP_COUNT) {
        this.currentStep = index;
        this.renderStep(this.currentStep);
      }
    }

    // Funnel-tracking events for GA4. Published via Shopify's Web Pixels
    // customer-events API (window.Shopify.analytics.publish) rather than a
    // direct gtag() call, because the storefront's GA4 tag runs inside
    // Shopify's sandboxed Web Pixels Manager - there's no global `gtag`
    // accessible to theme JS. A separate custom Web Pixel (added in Admin,
    // not part of this theme repo) subscribes to these same event names and
    // forwards them to GA4 via the Measurement Protocol.
    publishAnalytics(name, data = {}) {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    }

    // --- Rendering --------------------------------------------------------

    renderStep(index) {
      this.steps.forEach((step, i) => {
        const isActive = i === index;
        step.classList.toggle('is-active', isActive);
        step.hidden = !isActive;
      });

      // Result page hides both the fixed nav and progress bar (see below) -
      // this class drops the bottom padding reserved for them on mobile so
      // their absence doesn't just leave blank space instead.
      this.classList.toggle('fg-quiz--result-step', index === 8);

      this.updateProgress(index);
      this.updateNav(index);

      // Page 2's pill set depends on Page 1's line selection (Savory &
      // Spiced only shows if Garden Bowls survived), so re-render every time
      // this step becomes active rather than once at load.
      if (index === 1) this.renderFlavorPills();

      // Page 9 assembles the box from every prior answer, so it has to be
      // built fresh each time it's reached (e.g. after going Back and
      // changing something), not once at load.
      if (index === 8) this.renderResult();

      this.publishAnalytics('quiz_step_viewed', { step: index + 1, step_name: STEP_NAMES[index] });
    }

    updateProgress(index) {
      // Result page is terminal - the quiz is done, so the progress bar
      // (and its "Question X of 9" label) is just wasted vertical space there.
      if (this.progressContainer) this.progressContainer.hidden = index === 8;
      if (this.progressFill) {
        const pct = ((index + 1) / STEP_COUNT) * 100;
        this.progressFill.style.width = `${pct}%`;
      }
      if (this.progressLabel) {
        this.progressLabel.textContent = STEP_LABELS[index];
        this.progressLabel.hidden = STEP_LABELS[index] === '';
      }
    }

    updateNav(index) {
      // Page 9 (result) is terminal and has its own "Add These to My Cart"
      // CTA - hide the shared nav there entirely. Page 8 (email capture) has
      // its own primary action too (skip-confirmation modal / "See My Box"),
      // but still wants Back so the customer can return to Box Size.
      const isResultStep = index === 8;
      const usesGenericContinue = index < 7;

      if (this.navContainer) this.navContainer.hidden = isResultStep;
      if (this.backButton) this.backButton.hidden = index === 0 || isResultStep;
      if (this.continueButton) {
        this.continueButton.hidden = !usesGenericContinue;
        this.continueButton.disabled = usesGenericContinue && !this.isStepValid(index);
      }
    }

    // --- Validation (per the rules locked in during design) ---------------

    isStepValid(index) {
      switch (index) {
        case 0: // Page 1: at least one line must survive the edit step
          return this.answers.lines.length > 0;
        case 1: // Page 2: flavors optional, zero is valid ("no preference")
          return true;
        case 2: // Page 3: allergens/skip optional, hard filter either way
          return true;
        case 3: // Page 4: loved ingredients optional
          return true;
        case 4: // Page 5: health goals optional
          return true;
        case 5: // Page 6: required single-select, no default
          return this.answers.variety === 'mix' || this.answers.variety === 'favorites';
        case 6: // Page 7: required, minimum 8 (the real order minimum)
          return typeof this.answers.boxSize === 'number' && this.answers.boxSize >= 8;
        default:
          return true;
      }
    }

    // --- Page 1: When You'll Eat Them + Recommended Product Lines --------

    setupWhenAndLines() {
      this.whenPills = Array.from(this.querySelectorAll('[data-when]'));
      this.linesReveal = this.querySelector('[data-fg-quiz-lines-reveal]');
      this.lineCardsContainer = this.querySelector('[data-fg-quiz-line-cards]');

      this.whenPills.forEach((pill) => {
        pill.addEventListener('click', () => this.toggleWhen(pill));
      });
    }

    toggleWhen(pill) {
      const value = pill.dataset.when;
      const isSelected = pill.classList.toggle('is-selected');
      if (isSelected) {
        this.answers.when.push(value);
      } else {
        this.answers.when = this.answers.when.filter((w) => w !== value);
      }

      // Recommended lines are recomputed fresh from the current "when"
      // answers every time they change - any manual card edits the customer
      // made get reset to the new recommendation. Manual toggles only stick
      // between "when" changes, not across them.
      this.answers.lines = this.computeRecommendedLines();
      this.renderLineCards();
      this.updateNav(this.currentStep);
    }

    computeRecommendedLines() {
      const set = new Set();
      this.answers.when.forEach((w) => {
        (WHEN_TO_LINES[w] || []).forEach((line) => set.add(line));
      });
      return Array.from(set);
    }

    renderLineCards() {
      const hasAnyWhen = this.answers.when.length > 0;
      if (this.linesReveal) this.linesReveal.hidden = !hasAnyWhen;
      if (!hasAnyWhen || !this.lineCardsContainer) return;

      this.lineCardsContainer.innerHTML = '';

      LINE_ORDER.forEach((lineName) => {
        const line = this.data.lines[lineName];
        if (!line) return;

        const isSelected = this.answers.lines.includes(lineName);
        const representativeImage = line.products[0]?.image || '';

        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'fg-quiz__card' + (isSelected ? ' is-selected' : '');
        card.dataset.line = lineName;
        card.innerHTML = `
          ${representativeImage ? `<img class="fg-quiz__card-image" src="${representativeImage}" alt="${lineName}" loading="lazy">` : ''}
          <p class="fg-quiz__card-title">${lineName}</p>
          <p class="fg-quiz__card-copy">${line.description || ''}</p>
        `;
        card.addEventListener('click', () => this.toggleLine(lineName));
        this.lineCardsContainer.appendChild(card);
      });
    }

    toggleLine(lineName) {
      if (this.answers.lines.includes(lineName)) {
        this.answers.lines = this.answers.lines.filter((l) => l !== lineName);
      } else {
        this.answers.lines.push(lineName);
      }
      this.renderLineCards();
      this.updateNav(this.currentStep);
    }

    // --- Page 2: Flavor Profile -------------------------------------------

    setupFlavorProfile() {
      this.flavorPillsContainer = this.querySelector('[data-fg-quiz-flavor-pills]');
    }

    renderFlavorPills() {
      if (!this.flavorPillsContainer) return;

      const gardenBowlsSelected = this.answers.lines.includes('Garden Bowls');
      const availableTags = (this.data.taxonomies.flavor_profile || []).filter(
        (tag) => tag !== 'Savory & Spiced' || gardenBowlsSelected
      );

      // If Savory & Spiced was picked earlier but Garden Bowls got
      // deselected back on page 1, drop the now-invisible selection too.
      if (!gardenBowlsSelected) {
        this.answers.flavors = this.answers.flavors.filter((f) => f !== 'Savory & Spiced');
      }

      this.flavorPillsContainer.innerHTML = '';
      availableTags.forEach((tag) => {
        const isSelected = this.answers.flavors.includes(tag);
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'fg-quiz__pill' + (isSelected ? ' is-selected' : '');
        pill.textContent = tag;
        pill.addEventListener('click', () => this.toggleFlavor(tag, pill));
        this.flavorPillsContainer.appendChild(pill);
      });
    }

    toggleFlavor(tag, pill) {
      const isSelected = pill.classList.toggle('is-selected');
      if (isSelected) {
        this.answers.flavors.push(tag);
      } else {
        this.answers.flavors = this.answers.flavors.filter((f) => f !== tag);
      }
    }

    // --- Page 3: Dietary Needs & Restrictions ------------------------------

    setupDietaryNeeds() {
      this.allergenPillsContainer = this.querySelector('[data-fg-quiz-allergen-pills]');
      this.renderAllergenPills();

      // Facility cross-contact is a fixed manufacturing fact, not something
      // that should disappear from the disclosure just because a product
      // carrying that allergen isn't active in the Bundle collection right
      // now (e.g. a seasonal Hazelnut flavor between runs) - unlike the
      // pills above, this is intentionally NOT derived from live product data.
      const footnote = this.querySelector('[data-fg-quiz-facility-footnote]');
      if (footnote) {
        const facilityAllergens = ['Almond', 'Cashew', 'Hazelnut', 'Pecan', 'Walnut', 'Peanuts', 'Soy'];
        footnote.textContent = `All products are made in a facility that also processes: ${facilityAllergens.join(', ')}.`;
      }

      const skipDropdownContainer = this.querySelector('[data-fg-quiz-skip-ingredients-dropdown]');
      if (skipDropdownContainer) {
        this.skipIngredientsDropdown = this.createIngredientDropdown(skipDropdownContainer, {
          label: 'Filter By: Ingredients',
          modalTitle: 'Any ingredients to <strong>skip</strong>?',
          getSelected: () => this.answers.skipIngredients,
          getIngredientPool: () => this.skippableIngredientsTaxonomy(),
          onToggle: (ingredient) => {
            if (this.answers.skipIngredients.includes(ingredient)) {
              this.answers.skipIngredients = this.answers.skipIngredients.filter((i) => i !== ingredient);
            } else {
              this.answers.skipIngredients.push(ingredient);
              // Exclusion always beats a soft "loved" preference for the same
              // ingredient - drop it from Page 4's answers immediately too,
              // not just visually disable it there.
              this.answers.lovedIngredients = this.answers.lovedIngredients.filter((i) => i !== ingredient);
            }
            this.lovedIngredientsDropdown?.refresh();
          },
        });
      }
    }

    // --- Page 3 helper: ingredients to skip/avoid ---------------------------

    // Full ingredient list (no top-N cut - allergen/trace-ingredient
    // exclusion needs to stay complete for safety), narrowed to only the
    // lines actually selected on Page 1 - a Smoothies-only customer
    // shouldn't have to scroll past Garden-Bowls-only ingredients they
    // could never receive anyway.
    skippableIngredientsTaxonomy() {
      const set = new Set();
      const selectedLines = this.answers.lines.length ? this.answers.lines : Object.keys(this.data.lines || {});
      selectedLines.forEach((lineName) => {
        (this.data.lines[lineName]?.products || []).forEach((product) => {
          (product.ingredients || []).forEach((ing) => set.add(ing));
        });
      });
      return Array.from(set).sort();
    }

    // --- Page 4: Ingredients You Love --------------------------------------

    // Testers found the full ~94-ingredient taxonomy overwhelming for a
    // "what do you love" preference pick, but an earlier version of this
    // that auto-derived "primary" ingredients from each product's first 3
    // (nutrition-label descending-weight order) cut real flavor-driving
    // spices that happen to appear later in a product's ingredient list
    // (Allyson's example: "fun spices... that makes a difference on
    // smoothies"). Reads a manually-curated
    // metafields.custom.primary_flavor_ingredients list per product
    // instead - the ingredients that actually define how a flavor tastes,
    // Allyson's own judgment call per product rather than a positional
    // guess. Falls back to the top-3-by-label-order heuristic for any
    // product that hasn't been curated yet, so the picker still works
    // (just less precisely) during the rollout instead of showing nothing
    // for that product. Narrowed to only the lines selected on Page 1,
    // same reasoning as skippableIngredientsTaxonomy() above. Recomputed
    // fresh each time the modal opens (getIngredientPool is a callback,
    // not cached), so it stays in sync if the customer goes back and
    // changes their Page 1 line selection.
    loveableIngredientsTaxonomy() {
      const set = new Set();
      const selectedLines = this.answers.lines.length ? this.answers.lines : Object.keys(this.data.lines || {});
      selectedLines.forEach((lineName) => {
        (this.data.lines[lineName]?.products || []).forEach((product) => {
          const curated = product.primary_flavor_ingredients;
          const pool = curated && curated.length ? curated : (product.ingredients || []).slice(0, 3);
          pool.forEach((ing) => set.add(ing));
        });
      });
      return Array.from(set).sort();
    }

    setupLovedIngredients() {
      const container = this.querySelector('[data-fg-quiz-loved-ingredients-dropdown]');
      if (!container) return;

      this.lovedIngredientsDropdown = this.createIngredientDropdown(container, {
        label: 'Filter By: Ingredients',
        modalTitle: 'Which ingredients do you <strong>love</strong>?',
        getSelected: () => this.answers.lovedIngredients,
        getIngredientPool: () => this.loveableIngredientsTaxonomy(),
        onToggle: (ingredient) => {
          if (this.answers.lovedIngredients.includes(ingredient)) {
            this.answers.lovedIngredients = this.answers.lovedIngredients.filter((i) => i !== ingredient);
          } else {
            this.answers.lovedIngredients.push(ingredient);
          }
        },
        // Anything marked "skip" on page 3 can't also be marked "loved" here
        // - disabled rather than just hidden, so it's clear why it's missing.
        isDisabled: (ingredient) => this.answers.skipIngredients.includes(ingredient),
      });
    }

    // --- Page 5: Health Goals ----------------------------------------------

    setupHealthGoals() {
      const container = this.querySelector('[data-fg-quiz-health-pills]');
      if (!container) return;

      (this.data.taxonomies.health_claims || []).forEach((goal) => {
        const isSelected = this.answers.healthGoals.includes(goal);
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'fg-quiz__pill' + (isSelected ? ' is-selected' : '');
        pill.textContent = goal;
        pill.addEventListener('click', () => {
          const nowSelected = pill.classList.toggle('is-selected');
          if (nowSelected) {
            this.answers.healthGoals.push(goal);
          } else {
            this.answers.healthGoals = this.answers.healthGoals.filter((g) => g !== goal);
          }
        });
        container.appendChild(pill);
      });
    }

    // --- Page 6: Variety vs. Favorites --------------------------------------

    setupVarietyFavorites() {
      const cards = Array.from(this.querySelectorAll('[data-variety]'));
      cards.forEach((card) => {
        card.addEventListener('click', () => {
          this.answers.variety = card.dataset.variety;
          cards.forEach((c) => c.classList.toggle('is-selected', c === card));
          this.updateNav(this.currentStep);
        });
      });
    }

    // --- Page 7: Box Size ---------------------------------------------------

    setupBoxSize() {
      const cards = Array.from(this.querySelectorAll('[data-box-size]'));
      const customInput = this.querySelector('[data-fg-quiz-custom-box-input]');
      if (!cards.length) return;

      const selectCard = (card) => {
        cards.forEach((c) => c.classList.toggle('is-selected', c === card));
        if (customInput) customInput.hidden = card.dataset.boxSize !== 'custom';
      };

      cards.forEach((card) => {
        card.addEventListener('click', () => {
          selectCard(card);
          if (card.dataset.boxSize === 'custom') {
            customInput?.focus();
            const parsed = parseInt(customInput?.value, 10);
            this.answers.boxSize = Number.isNaN(parsed) ? null : parsed;
          } else {
            this.answers.boxSize = parseInt(card.dataset.boxSize, 10);
          }
          this.updateNav(this.currentStep);
        });
      });

      customInput?.addEventListener('input', () => {
        const parsed = parseInt(customInput.value, 10);
        this.answers.boxSize = Number.isNaN(parsed) ? null : parsed;
        this.updateNav(this.currentStep);
      });
      // Typing in the number field shouldn't also re-fire the card's own
      // click handler via bubbling and stomp the just-typed value.
      customInput?.addEventListener('click', (e) => e.stopPropagation());
    }

    // --- Page 8: Email Capture ----------------------------------------------

    setupEmailCapture() {
      const nameInput = this.querySelector('[data-fg-quiz-name-input]');
      const emailInput = this.querySelector('[data-fg-quiz-email-input]');
      const phoneInput = this.querySelector('[data-fg-quiz-phone-input]');
      const seeMyBoxButton = this.querySelector('[data-fg-quiz-see-my-box]');
      const skipLink = this.querySelector('[data-fg-quiz-skip-email]');
      const modal = this.querySelector('[data-fg-quiz-skip-modal]');
      const closeButtons = this.querySelectorAll('[data-fg-quiz-skip-modal-close]');
      const enterEmailButton = this.querySelector('[data-fg-quiz-enter-email]');
      const skipAnywayButton = this.querySelector('[data-fg-quiz-skip-anyway]');

      nameInput?.addEventListener('input', () => {
        this.answers.firstName = nameInput.value;
      });
      emailInput?.addEventListener('input', () => {
        this.answers.email = emailInput.value;
      });
      phoneInput?.addEventListener('input', () => {
        this.answers.phone = phoneInput.value;
      });

      seeMyBoxButton?.addEventListener('click', () => {
        if (!emailInput || !emailInput.checkValidity()) {
          emailInput?.reportValidity();
          return;
        }
        this.answers.email = emailInput.value;
        this.publishAnalytics('quiz_email_captured', { has_phone: !!this.answers.phone });
        this.pushToKlaviyo();
        this.goToStep(8);
      });

      const closeModal = () => {
        if (modal) modal.hidden = true;
      };

      skipLink?.addEventListener('click', () => {
        if (modal) modal.hidden = false;
      });
      closeButtons.forEach((btn) => btn.addEventListener('click', closeModal));
      enterEmailButton?.addEventListener('click', () => {
        closeModal();
        emailInput?.focus();
      });
      skipAnywayButton?.addEventListener('click', () => {
        closeModal();
        this.publishAnalytics('quiz_skipped_email');
        this.goToStep(8);
      });
    }

    // Klaviyo flows have no "Subscribe to Marketing" action of their own -
    // consent has to be set before the flow's trigger event fires, via the
    // Client API's public subscribe endpoint (safe for storefront code, no
    // private key involved). So this handles both identify()/track() *and*
    // the actual subscribe call, rather than assuming the flow builder
    // handles consent on its own.
    subscribeToKlaviyoMarketing() {
      const profileAttributes = {
        email: this.answers.email,
        subscriptions: {
          email: { marketing: { consent: 'SUBSCRIBED' } },
        },
      };
      if (this.answers.firstName) profileAttributes.first_name = this.answers.firstName;
      if (this.answers.phone) {
        profileAttributes.phone_number = toE164UsPhone(this.answers.phone);
        profileAttributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }

      // Klaviyo's subscription resource takes the profile nested under
      // attributes.profile.data (JSON:API style) and the list as a
      // relationship, not flat fields on attributes directly - confirmed
      // against the API's own validation errors, since Klaviyo's public
      // docs page didn't show a literal request-body example to copy.
      fetch('https://a.klaviyo.com/client/subscriptions?company_id=Lp9UmH', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-04-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: profileAttributes } },
            },
            relationships: {
              list: { data: { type: 'list', id: 'MiRNBw' } }, // Master Email List - single opt-in, takes effect immediately
            },
          },
        }),
      });
    }

    // Identifies the Klaviyo profile and fires a custom event carrying the
    // full quiz answers, so a follow-up flow can personalize off their
    // actual picks (e.g. "since you love Tropical flavors..."). Only called
    // from the "See My Box" path - "Skip Anyway" stays fully anonymous, no
    // identify/track/subscribe call at all. Uses the site's existing onsite
    // Klaviyo JS (already installed sitewide, company_id Lp9UmH) - no new
    // script include needed. NOTE: the actual Klaviyo flow still needs to be
    // built in Klaviyo's own UI (triggering off "Completed Flavor Quiz") -
    // that's a marketing-ops task, not part of this theme code. Consent
    // itself, though, is fully handled here - the flow doesn't need to do
    // anything about subscription.
    //
    // Also computes the actual result (buildResult()) so the flow can list
    // the real products/quantities and include a one-click link straight
    // into the bundle builder with those exact items prepopulated - if the
    // customer doesn't order right away, a follow-up email still has a
    // working "complete your box" link, not just their raw preferences.
    // Safe to compute this early (before Page 9 itself renders it): it's a
    // pure function of this.answers/this.data, no DOM side effects, and
    // renderResult() will compute the identical result again a moment
    // later when Page 9 actually shows - same answers, same output.
    pushToKlaviyo() {
      if (typeof klaviyo === 'undefined') return;

      const result = this.buildResult();
      const boxItems = result.empty
        ? []
        : result.items.map(({ product, qty, reasoning }) => ({
            title: product.product_type ? `${product.title} ${product.product_type}` : product.title,
            qty,
            // Shopify's image_url filter returns protocol-relative URLs
            // (//cdn...) - fine on a live webpage (inherits the page's own
            // protocol), but most email clients have no "page" to inherit
            // from and just fail to load them. Force https: explicitly.
            image: product.image && product.image.startsWith('//') ? `https:${product.image}` : product.image,
            price: product.price,
            url: `${window.location.origin}/products/${product.handle}`,
            reasoning,
          }));
      // Pre-chunked into rows of 2 so the Klaviyo email template can do a
      // plain nested loop, rather than relying on modulo/index math inside
      // Klaviyo's Liquid dialect (which doesn't behave the same as Shopify's).
      const boxItemRows = [];
      for (let i = 0; i < boxItems.length; i += 2) {
        boxItemRows.push(boxItems.slice(i, i + 2));
      }
      const boxUrl = result.empty
        ? ''
        : `${window.location.origin}/products/custom-bundle?bundle_items=${encodeURIComponent(
            result.items.map(({ product, qty }) => `${product.id}:${qty}`).join(',')
          )}`;

      // Same answers go to both calls, for two different purposes:
      // - on the identify() call, they become persistent custom Profile
      //   Fields (Klaviyo auto-creates these on first use, nothing to
      //   pre-configure in Klaviyo's UI) - always reflects their LATEST
      //   quiz answers, so segmentation/personalization stays current even
      //   if they retake the quiz later.
      // - on the track() call, they're event properties tied to this one
      //   "Completed Flavor Quiz" occurrence - used for flow-triggering
      //   logic and for referencing "what they just answered" inside the
      //   specific email that flow sends.
      // Property keys are space-free (QuizBoxItems, not "Quiz - Box Items")
      // so they can be referenced in Klaviyo's Liquid with plain dot
      // notation everywhere - including inside {% for %} loops, where the
      // |lookup: filter needed for spaced names isn't reliably supported.
      const quizProperties = {
        QuizProductLines: this.answers.lines,
        QuizWhen: this.answers.when,
        QuizFlavorProfile: this.answers.flavors,
        QuizAllergens: this.answers.allergens,
        QuizIngredientsToSkip: this.answers.skipIngredients,
        QuizLovedIngredients: this.answers.lovedIngredients,
        QuizHealthGoals: this.answers.healthGoals,
        QuizVarietyPreference: this.answers.variety,
        QuizBoxSize: this.answers.boxSize,
        QuizBoxItems: boxItems,
        QuizBoxItemRows: boxItemRows,
        QuizBoxUrl: boxUrl,
        QuizItemCount: boxItems.length, // lets the flow filter out empty-result submissions before the box-reveal email
      };

      const profile = { email: this.answers.email, $source: 'Quiz', ...quizProperties };
      if (this.answers.firstName) profile.first_name = this.answers.firstName;
      if (this.answers.phone) profile.phone_number = toE164UsPhone(this.answers.phone);

      this.subscribeToKlaviyoMarketing();

      klaviyo.identify(profile).then(() => {
        klaviyo.track('Completed Flavor Quiz', quizProperties);
      });
    }

    // --- Page 9: Result ------------------------------------------------------
    // Implements the scoring algorithm from the design spec: split box size
    // across lines (Smoothies-heavy default), hard-filter by allergens/
    // skipped ingredients (plus the Lunch-only protein restriction), score by
    // flavor/ingredient/health-goal matches, allocate quantity per the
    // Mix It Up vs Stick With Favorites tiebreaker, and redistribute a whole
    // line's target if its pool empties out. Falls back to an honest empty
    // state (no auto-substituted lines) if nothing qualifies at all.

    renderResult() {
      const container = this.querySelector('[data-fg-quiz-result]');
      if (!container) return;

      const result = this.buildResult();
      container.innerHTML = '';

      this.publishAnalytics('quiz_completed', { empty: result.empty });

      if (result.empty) {
        container.innerHTML = `
          <h2 class="fg-quiz__step-headline">Hmm, we <strong>couldn't find a match</strong>.</h2>
          <p class="fg-quiz__step-subhead">Your restrictions narrowed things down more than we could work with — no flavors fit every requirement.</p>
          <div class="fg-quiz__result-empty-actions">
            <button type="button" class="fg-quiz__nav-button fg-quiz__nav-button--continue" data-fg-quiz-retake>Retake the Quiz</button>
            <a href="/products/custom-bundle" class="fg-quiz__nav-button fg-quiz__nav-button--secondary-filled">Build Your Own Box</a>
          </div>
          <button type="button" class="fg-quiz__nav-button fg-quiz__nav-button--back fg-quiz__result-back" data-fg-quiz-result-back>← Back to Change Answers</button>
        `;
        container.querySelector('[data-fg-quiz-retake]')?.addEventListener('click', () => {
          window.location.reload();
        });
        container.querySelector('[data-fg-quiz-result-back]')?.addEventListener('click', () => {
          this.goBack();
        });
        return;
      }

      const backLink = document.createElement('button');
      backLink.type = 'button';
      backLink.className = 'fg-quiz__nav-button fg-quiz__nav-button--back fg-quiz__result-back';
      backLink.textContent = '← Back to Change Answers';
      backLink.addEventListener('click', () => this.goBack());

      const heading = document.createElement('h2');
      heading.className = 'fg-quiz__step-headline';
      heading.innerHTML = 'Here’s What We <strong>Picked For You</strong>';
      const sub = document.createElement('p');
      sub.className = 'fg-quiz__step-subhead';
      sub.textContent = 'Based on your answers, we think you’ll love these.';

      const list = document.createElement('div');
      list.className = 'fg-quiz__result-items';
      result.items.forEach(({ product, qty, reasoning }) => {
        const row = document.createElement('div');
        row.className = 'fg-quiz__result-item';
        const displayTitle = product.product_type ? `${product.title} ${product.product_type}` : product.title;
        row.innerHTML = `
          ${product.image ? `<img class="fg-quiz__result-item-image" src="${product.image}" alt="${displayTitle}" loading="lazy">` : ''}
          <div>
            <p class="fg-quiz__result-item-title">${displayTitle}</p>
            <p class="fg-quiz__result-item-qty">Qty: ${qty}</p>
            <p class="fg-quiz__result-item-reason">${reasoning}</p>
          </div>
        `;
        list.appendChild(row);
      });

      const note = document.createElement('p');
      note.className = 'fg-quiz__result-note';
      note.textContent = "Don't love a pick? No problem — you can add, remove, or swap anything once it's in your cart.";

      const nutritionLink = document.createElement('button');
      nutritionLink.type = 'button';
      nutritionLink.className = 'fg-quiz__result-nutrition-link';
      nutritionLink.textContent = 'View Nutrition Info for Your Box';
      nutritionLink.addEventListener('click', () => this.openNutritionInfoModal(result.items));

      const actions = document.createElement('div');
      actions.className = 'fg-quiz__result-actions';

      const cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'fg-quiz__nav-button fg-quiz__nav-button--continue';
      cta.textContent = 'Add These to My Cart';
      cta.addEventListener('click', () => {
        const bundleItems = result.items.map(({ product, qty }) => `${product.id}:${qty}`).join(',');
        window.location.href = `/products/custom-bundle?bundle_items=${encodeURIComponent(bundleItems)}`;
      });

      const retakeButton = document.createElement('button');
      retakeButton.type = 'button';
      retakeButton.className = 'fg-quiz__nav-button fg-quiz__nav-button--secondary-filled';
      retakeButton.textContent = 'Retake Quiz';
      retakeButton.addEventListener('click', () => {
        window.location.reload();
      });

      actions.appendChild(cta);
      actions.appendChild(retakeButton);

      container.appendChild(heading);
      container.appendChild(sub);
      container.appendChild(list);
      container.appendChild(note);
      container.appendChild(nutritionLink);
      container.appendChild(actions);
      container.appendChild(backLink);
    }

    openNutritionInfoModal(items) {
      this.querySelector('[data-fg-quiz-nutrition-modal]')?.remove();

      const modal = document.createElement('div');
      modal.className = 'fg-quiz__modal fg-quiz__modal--nutrition';
      modal.setAttribute('data-fg-quiz-nutrition-modal', '');

      const overlay = document.createElement('div');
      overlay.className = 'fg-quiz__modal-overlay';

      const content = document.createElement('div');
      content.className = 'fg-quiz__modal-content fg-quiz__modal-content--nutrition';

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'fg-quiz__modal-close';
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.textContent = '×';

      const title = document.createElement('h3');
      title.className = 'fg-quiz__modal-title';
      title.textContent = 'Nutrition Info for Your Box';

      const list = document.createElement('div');
      list.className = 'fg-quiz__nutrition-modal-list';
      list.innerHTML = items
        .map(({ product, qty }) => {
          const displayTitle = product.product_type ? `${product.title} ${product.product_type}` : product.title;
          const nutritionMarkup = product.nutrition_image
            ? `<a href="${product.nutrition_image}" target="_blank" rel="noopener" class="fg-quiz__nutrition-modal-item-image-link"><img src="${product.nutrition_image}" alt="Nutrition Facts for ${this.escapeHtml(displayTitle)}" loading="lazy"></a>`
            : `<p class="fg-quiz__nutrition-modal-item-empty">Nutrition facts coming soon.</p>`;
          return `
            <details class="fg-quiz__nutrition-modal-item">
              <summary>
                <span class="fg-quiz__nutrition-modal-item-title">${this.escapeHtml(displayTitle)}</span>
                ${qty ? `<span class="fg-quiz__nutrition-modal-item-qty">x${this.escapeHtml(qty)}</span>` : ''}
              </summary>
              <div class="fg-quiz__nutrition-modal-item-content">${nutritionMarkup}</div>
            </details>
          `;
        })
        .join('');

      content.appendChild(closeButton);
      content.appendChild(title);
      content.appendChild(list);
      modal.appendChild(overlay);
      modal.appendChild(content);
      this.appendChild(modal);

      const closeModal = () => {
        modal.remove();
        document.body.style.overflow = '';
      };
      closeButton.addEventListener('click', closeModal);
      overlay.addEventListener('click', closeModal);

      document.body.style.overflow = 'hidden';
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    buildResult() {
      const lineNames = [...this.answers.lines];
      if (!lineNames.length || !this.answers.boxSize) return { empty: true };

      let targets = this.splitBoxSize(lineNames, this.answers.boxSize);

      const candidatesByLine = {};
      lineNames.forEach((line) => {
        candidatesByLine[line] = this.filterCandidates(line);
      });

      const nonEmptyLines = lineNames.filter((l) => candidatesByLine[l].length > 0);
      const emptyLines = lineNames.filter((l) => candidatesByLine[l].length === 0);

      if (!nonEmptyLines.length) return { empty: true };

      if (emptyLines.length) {
        const emptyTotal = emptyLines.reduce((sum, l) => sum + (targets[l] || 0), 0);
        const nonEmptyTotal = nonEmptyLines.reduce((sum, l) => sum + (targets[l] || 0), 0) || 1;
        emptyLines.forEach((l) => {
          targets[l] = 0;
        });
        let distributed = 0;
        nonEmptyLines.forEach((l, i) => {
          const isLast = i === nonEmptyLines.length - 1;
          const share = isLast ? emptyTotal - distributed : Math.round((emptyTotal * (targets[l] || 0)) / nonEmptyTotal);
          distributed += share;
          targets[l] = (targets[l] || 0) + share;
        });
      }

      const items = [];
      nonEmptyLines.forEach((line) => {
        const scored = this.scoreCandidates(candidatesByLine[line], line);
        const allocated = this.allocateWithinLine(scored, targets[line] || 0, line);
        allocated.forEach(({ product, qty }) => {
          items.push({ product, qty, reasoning: this.buildReasoning(product) });
        });
      });

      return { empty: false, items };
    }

    // Step 1: split N across lines - Smoothies gets 65% when selected
    // alongside other lines (87% of revenue, per the design memory's
    // default), 100% if it's the only line, otherwise an even split.
    splitBoxSize(lineNames, N) {
      const targets = {};
      const hasSmoothies = lineNames.includes('Smoothies');

      if (hasSmoothies && lineNames.length > 1) {
        const smoothiesQty = Math.round(N * 0.65);
        targets.Smoothies = smoothiesQty;
        const others = lineNames.filter((l) => l !== 'Smoothies');
        const remaining = N - smoothiesQty;
        const base = Math.floor(remaining / others.length);
        const extra = remaining - base * others.length;
        others.forEach((line, i) => {
          targets[line] = base + (i < extra ? 1 : 0);
        });
      } else if (hasSmoothies) {
        targets.Smoothies = N;
      } else {
        const base = Math.floor(N / lineNames.length);
        const extra = N - base * lineNames.length;
        lineNames.forEach((line, i) => {
          targets[line] = base + (i < extra ? 1 : 0);
        });
      }
      return targets;
    }

    // Which "when" answers pulled a given line into the final set - needed
    // for the Lunch/protein-only restriction below.
    lineReasons(line) {
      return this.answers.when.filter((w) => (WHEN_TO_LINES[w] || []).includes(line));
    }

    // Step 2: hard filter by allergens/skipped ingredients, plus the
    // Lunch-only protein restriction on Smoothies (resolved during design:
    // only applies if Lunch is the SOLE reason Smoothies is selected - if
    // Breakfast or Snacks also pulled it in, the restriction drops).
    filterCandidates(line) {
      const products = this.data.lines[line]?.products || [];
      let candidates = products.filter((p) => {
        const hitAllergen = (p.allergens || []).some((a) => this.answers.allergens.includes(a));
        if (hitAllergen) return false;
        const hitSkip = (p.ingredients || []).some((i) => this.answers.skipIngredients.includes(i));
        return !hitSkip;
      });

      if (line === 'Smoothies') {
        const reasons = this.lineReasons('Smoothies');
        const lunchOnly = reasons.length === 1 && reasons[0] === 'Lunch';
        if (lunchOnly) {
          candidates = candidates.filter((p) => (p.health_claims || []).includes('Protein Rich'));
        }
      }
      return candidates;
    }

    // Step 3: score by flavor/loved-ingredient/health-goal matches, equal
    // weighting. Tiebreak by the Bundle collection's own curated product
    // order (a reasonable stand-in for best-seller rank, which isn't in the
    // data payload) rather than randomly.
    scoreCandidates(candidates, line) {
      return candidates
        .map((product, index) => {
          let score = 0;
          score += (product.flavor_profile || []).filter((f) => this.answers.flavors.includes(f)).length;
          score += (product.ingredients || []).filter((i) => this.answers.lovedIngredients.includes(i)).length;
          score += (product.health_claims || []).filter((h) => this.answers.healthGoals.includes(h)).length;
          return { product, score, index };
        })
        .sort((a, b) => b.score - a.score || a.index - b.index);
    }

    // Step 4: cap distinct products by the line's own target quantity, then
    // spread (Mix It Up) or concentrate (Stick With Favorites) within that
    // cap - per the finalized algorithm spec. Smoothies gets its own tier
    // table (targeting a specific max qty-per-item "ceiling" at each box
    // size, then deriving distinct count from that) instead of the shared
    // table below - it's the one line with a genuinely deep catalog (20+
    // flavors), tuned so bigger boxes still show real variety without
    // needing more distinct flavors than the catalog realistically supports.
    // Mix and Favorites ceilings don't scale in a fixed ratio to each other
    // here (e.g. targetQty 15 is 15 distinct for Mix but only 5 for
    // Favorites - not a clean half), so this returns both counts directly
    // rather than the generic path's single number that gets halved for
    // Favorites in allocateWithinLine. Actual candidate-pool size still
    // limits it in practice (allocateWithinLine already redistributes if
    // fewer products qualify).
    distinctCap(targetQty, line) {
      if (line === 'Smoothies') {
        let mixCeiling;
        let favoritesCeiling;
        if (targetQty <= 8) {
          mixCeiling = 1;
          favoritesCeiling = 2;
        } else if (targetQty <= 15) {
          mixCeiling = 1;
          favoritesCeiling = 3;
        } else if (targetQty <= 24) {
          mixCeiling = 2;
          favoritesCeiling = 4;
        } else {
          mixCeiling = 3;
          favoritesCeiling = 5;
        }
        return {
          mix: Math.max(1, Math.ceil(targetQty / mixCeiling)),
          favorites: Math.max(1, Math.ceil(targetQty / favoritesCeiling)),
        };
      }
      let generic;
      if (targetQty <= 3) generic = 2;
      else if (targetQty <= 8) generic = 4;
      else if (targetQty <= 15) generic = 6;
      else if (targetQty <= 23) generic = 8;
      else if (targetQty <= 35) generic = 10;
      else generic = 12;
      return { mix: generic, favorites: Math.max(1, Math.round(generic / 2)) };
    }

    allocateWithinLine(scored, targetQty, line) {
      if (targetQty <= 0 || !scored.length) return [];

      const caps = this.distinctCap(targetQty, line);
      const isMix = this.answers.variety === 'mix';
      const maxDistinct = isMix ? caps.mix : caps.favorites;
      const pool = scored.slice(0, Math.min(maxDistinct, scored.length));
      const n = pool.length;
      const allocations = new Array(n).fill(0);

      if (isMix) {
        const base = Math.floor(targetQty / n);
        const extra = targetQty - base * n;
        for (let i = 0; i < n; i++) allocations[i] = base + (i < extra ? 1 : 0);
      } else {
        // Favorites: allocate proportional to score (+1 so a 0-score fringe
        // pick still gets a baseline share) instead of always handing the
        // catalog-order "winner" a flat 60% - a genuine standout (higher
        // score than the rest) still gets noticeably more, but products
        // that are tied on score (e.g. everything just matches the same
        // single flavor tag) come out even instead of one arbitrarily
        // getting way more than the others.
        const weights = pool.map((entry) => entry.score + 1);
        const totalWeight = weights.reduce((sum, w) => sum + w, 0);
        const raw = weights.map((w) => (targetQty * w) / totalWeight);
        const floors = raw.map(Math.floor);
        const distributed = floors.reduce((sum, f) => sum + f, 0);
        let remainder = targetQty - distributed;
        const byFraction = raw
          .map((r, i) => ({ i, frac: r - floors[i] }))
          .sort((a, b) => b.frac - a.frac);
        for (let i = 0; i < n; i++) allocations[i] = floors[i];
        for (let k = 0; k < remainder; k++) allocations[byFraction[k].i] += 1;
      }

      return pool.map((entry, i) => ({ ...entry, qty: allocations[i] })).filter((e) => e.qty > 0);
    }

    // Step 5: one-line "why we picked this" reasoning citing the top matched
    // signals.
    buildReasoning(product) {
      const matchedFlavors = (product.flavor_profile || []).filter((f) => this.answers.flavors.includes(f));
      const matchedIngredients = (product.ingredients || []).filter((i) => this.answers.lovedIngredients.includes(i));
      const matchedHealth = (product.health_claims || []).filter((h) => this.answers.healthGoals.includes(h));

      const reasons = [];
      if (matchedFlavors.length) reasons.push(`you love ${matchedFlavors[0]} flavors`);
      if (matchedIngredients.length) reasons.push(`it has ${matchedIngredients[0]}`);
      if (matchedHealth.length) reasons.push(`it's ${matchedHealth[0]}`);

      if (!reasons.length) return 'A pick we think you’ll love.';
      return `Because ${reasons.slice(0, 2).join(' and ')}.`;
    }

    // Live taxonomy is alphabetical by default; tree nuts read better
    // grouped together first, with Peanuts/Soy/Caffeine trailing.
    sortedAllergens() {
      const priority = ['Almond', 'Cashew', 'Hazelnut', 'Pecan', 'Walnut', 'Peanut', 'Soy', 'Caffeine'];
      const rank = (tag) => (priority.indexOf(tag) === -1 ? priority.length : priority.indexOf(tag));
      return [...(this.data.taxonomies.allergens || [])].sort((a, b) => rank(a) - rank(b));
    }

    renderAllergenPills() {
      if (!this.allergenPillsContainer) return;
      this.allergenPillsContainer.innerHTML = '';

      this.sortedAllergens().forEach((allergen) => {
        const isSelected = this.answers.allergens.includes(allergen);
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'fg-quiz__filter-pill' + (isSelected ? ' is-selected' : '');
        pill.textContent = allergen;
        pill.addEventListener('click', () => {
          const nowSelected = pill.classList.toggle('is-selected');
          if (nowSelected) {
            this.answers.allergens.push(allergen);
          } else {
            this.answers.allergens = this.answers.allergens.filter((a) => a !== allergen);
          }
        });
        this.allergenPillsContainer.appendChild(pill);
      });
    }

    // Shared dropdown/checklist builder, reused by page 3's "skip" list and
    // page 4's "loved" list (same underlying ingredient taxonomy, opposite
    // hard-filter vs. soft-boost meaning). Matches the real bundle builder's
    // ingredient-filter pattern: removable chips for active selections above
    // the control, and pill buttons (not checkboxes) inside the panel.
    // Returns a handle with .refresh() so cross-page state changes (e.g. an
    // ingredient newly marked "skip") can force the other instance to
    // re-render its disabled state.
    // Opens as a modal rather than an anchored dropdown panel - feedback
    // from testers (both mobile and desktop) was that scrolling/tapping
    // through a long pill list inside a small anchored panel was confusing.
    // A modal gives the list room to breathe and lets someone multi-select
    // as many ingredients as they want before explicitly closing it with
    // Done, rather than the panel auto-closing on every outside tap.
    createIngredientDropdown(container, { label, modalTitle, getSelected, onToggle, onClear, isDisabled, getIngredientPool }) {
      container.innerHTML = '';

      const activeFilters = document.createElement('div');
      activeFilters.className = 'fg-quiz__active-filters';

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'fg-quiz__dropdown-toggle';

      container.appendChild(activeFilters);
      container.appendChild(toggle);

      const modal = document.createElement('div');
      modal.className = 'fg-quiz__modal fg-quiz__modal--ingredients';
      modal.hidden = true;

      const overlay = document.createElement('div');
      overlay.className = 'fg-quiz__modal-overlay';

      const content = document.createElement('div');
      content.className = 'fg-quiz__modal-content fg-quiz__modal-content--ingredients';

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'fg-quiz__modal-close';
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.textContent = '×';

      const title = document.createElement('h3');
      title.className = 'fg-quiz__modal-title';
      title.innerHTML = modalTitle || label;

      const search = document.createElement('input');
      search.type = 'text';
      search.className = 'fg-quiz__dropdown-search';
      search.placeholder = 'Search ingredients...';

      const list = document.createElement('div');
      list.className = 'fg-quiz__ingredient-modal-list';

      const doneButton = document.createElement('button');
      doneButton.type = 'button';
      doneButton.className = 'fg-quiz__nav-button fg-quiz__nav-button--continue fg-quiz__ingredient-modal-done';
      doneButton.textContent = 'Done';

      content.appendChild(closeButton);
      content.appendChild(title);
      content.appendChild(search);
      content.appendChild(list);
      content.appendChild(doneButton);
      modal.appendChild(overlay);
      modal.appendChild(content);
      this.appendChild(modal);

      const updateToggleLabel = () => {
        const count = getSelected().length;
        toggle.textContent = count > 0 ? `${label} (${count} selected)` : `${label} ▾`;
      };

      const renderActiveFilters = () => {
        activeFilters.innerHTML = '';
        const selected = getSelected();
        if (!selected.length) return;

        selected.forEach((ing) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'fg-quiz__active-filter-chip';
          chip.innerHTML = `${ing} <span aria-hidden="true">×</span>`;
          chip.addEventListener('click', () => {
            onToggle(ing);
            renderActiveFilters();
            updateToggleLabel();
            if (!modal.hidden) renderList(search.value);
          });
          activeFilters.appendChild(chip);
        });

        const removeAll = document.createElement('button');
        removeAll.type = 'button';
        removeAll.className = 'fg-quiz__remove-all';
        removeAll.textContent = 'Remove All';
        removeAll.addEventListener('click', () => {
          [...selected].forEach((ing) => onToggle(ing));
          onClear?.();
          renderActiveFilters();
          updateToggleLabel();
          if (!modal.hidden) renderList(search.value);
        });
        activeFilters.appendChild(removeAll);
      };

      const renderList = (filterText) => {
        list.innerHTML = '';
        const pool = getIngredientPool ? getIngredientPool() : this.data.taxonomies.ingredients || [];
        const items = pool.filter((ing) =>
          ing.toLowerCase().includes((filterText || '').toLowerCase())
        );
        items.forEach((ing) => {
          const disabled = isDisabled ? isDisabled(ing) : false;
          const pill = document.createElement('button');
          pill.type = 'button';
          const isSelected = getSelected().includes(ing);
          pill.className = 'fg-quiz__filter-pill' + (isSelected ? ' is-selected' : '') + (disabled ? ' is-disabled' : '');
          pill.textContent = ing;
          pill.disabled = disabled;
          pill.addEventListener('click', () => {
            onToggle(ing);
            updateToggleLabel();
            renderActiveFilters();
            renderList(search.value);
          });
          list.appendChild(pill);
        });
      };

      const openModal = () => {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        renderList(search.value);
      };
      const closeModal = () => {
        modal.hidden = true;
        document.body.style.overflow = '';
      };

      toggle.addEventListener('click', openModal);
      closeButton.addEventListener('click', closeModal);
      overlay.addEventListener('click', closeModal);
      doneButton.addEventListener('click', closeModal);
      search.addEventListener('input', () => renderList(search.value));

      updateToggleLabel();
      renderActiveFilters();

      return {
        refresh: () => {
          updateToggleLabel();
          renderActiveFilters();
          if (!modal.hidden) renderList(search.value);
        },
      };
    }

    // renderLovedIngredients() {} // Page 4
    // renderHealthGoals() {}    // Page 5
    // renderVarietyFavorites() {} // Page 6
    // renderBoxSize() {}        // Page 7
    // renderEmailCapture() {}   // Page 8
    // renderResult() {}         // Page 9 - builds bundle_items= and hands off
    //                           // to the existing bundle builder, per
    //                           // WIQBundleBuilder.loadFromUrlParams()
  }

  customElements.define('fg-flavor-quiz', FGFlavorQuiz);
})();
